import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'

const PROGRAM_COLOR = {
  slate:   { bg: '#F1EFE8', color: '#5F5E5A', solid: '#5F5E5A' },
  blue:    { bg: '#E6F1FB', color: '#185FA5', solid: '#378ADD' },
  violet:  { bg: '#F3E8FF', color: '#6B21A8', solid: '#7C3AED' },
  emerald: { bg: '#EAF3DE', color: '#3B6D11', solid: '#65A30D' },
  amber:   { bg: '#FAEEDA', color: '#854F0B', solid: '#D97706' },
}

export default function Programs() {
  const navigate = useNavigate()
  const [programs, setPrograms] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => { fetchPrograms() }, [])

  async function fetchPrograms() {
    // program_stats view does the heavy lifting server-side
    const [progsRes, statsRes] = await Promise.all([
      supabase.from('programs').select('*').order('sort_order'),
      supabase.from('program_stats').select('*'),
    ])
    const stats = {}
    ;(statsRes.data || []).forEach(s => { stats[s.id] = s })
    const withStats = (progsRes.data || []).map(p => ({ ...p, stats: stats[p.id] || {} }))
    setPrograms(withStats)
    setLoading(false)
  }

  if (loading) return <div className="spinner" />

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">Programs</div>
          <div className="page-subtitle">Three parallel initiatives across the ModWash fleet</div>
        </div>
      </div>

      {programs.map(p => {
        const color = PROGRAM_COLOR[p.color] || PROGRAM_COLOR.slate
        const s = p.stats
        return (
          <div key={p.id} className="card"
            style={{ marginBottom: 12, cursor: 'pointer', padding: 16 }}
            onClick={() => navigate(`/programs/${p.slug}`)}>
            <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
              <div style={{
                width: 48, height: 48, borderRadius: 10,
                background: color.solid, color: 'white',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 16, fontWeight: 700, flexShrink: 0,
              }}>
                {p.slug === 'pa-phase1' ? 'PA' : p.slug === 'amp-migration' ? 'AMP' : 'MAIN'}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                  <div style={{ fontSize: 15, fontWeight: 600 }}>{p.name}</div>
                  <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"
                    style={{ width: 18, height: 18, color: 'var(--text-muted)', flexShrink: 0, marginTop: 4 }}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                  </svg>
                </div>
                <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 2, lineHeight: 1.4 }}>
                  {p.description}
                </div>
                <div style={{
                  display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)',
                  gap: 8, marginTop: 12,
                }}>
                  <StatMini label="Sites"        value={s.site_count ?? 0} />
                  <StatMini label="Open"         value={s.open_ticket_count ?? 0} />
                  <StatMini label="Critical"     value={s.critical_open ?? 0} color={s.critical_open > 0 ? 'var(--red)' : undefined} />
                  <StatMini label="Dispatches"   value={s.dispatch_count ?? 0} />
                </div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 10 }}>
                  {formatDate(p.start_date)} – {formatDate(p.end_date)}
                </div>
              </div>
            </div>
          </div>
        )
      })}

      {programs.length === 0 && (
        <div className="empty-state">
          <div className="empty-state-icon">📋</div>
          <h3>No programs yet</h3>
          <p>An admin can add programs from the Admin panel.</p>
        </div>
      )}
    </div>
  )
}

function StatMini({ label, value, color }) {
  return (
    <div>
      <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{label}</div>
      <div style={{ fontSize: 18, fontWeight: 600, color: color || 'var(--text)' }}>{value}</div>
    </div>
  )
}

function formatDate(d) {
  if (!d) return '—'
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

export { PROGRAM_COLOR }
